import { FC, useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";

import characterCountInit from "form-pattern/src/scripts/form-character-count";
import InfoIcon from "../../../../../../../public/assets/images/info-circle-small-solid.svg";
import uploadOutline from "../../../../../../../public/assets/images/uploadOutline.svg";
import downloadOutline from "../../../../../../../public/assets/images/downloadOutline.svg";
import { useDispatch } from "react-redux";
import CardTable from "../../../../../../components/common/CardTable";
import Table from "../../../../../../components/common/Table/Table";
import Notifications from "../../../../../../components/common/Notifications";
import { validateForm } from "../../../../../../utils/validationEngine";
import { uploadDocumentFields } from "../../../../../../config/viewDocumentsFields";
import Loader from "../../../../../../components/common/Loader";
import UploadErrorBanner from "../../../../../CitizenshipReimbursement/UploadDocument/ViewDetails/UploadErrorBanner";
import { useAppSelector } from "../../../../../../store/hooks";
import { formatTimestampMDYHMS } from "../../../../../../utils/date";
import {
  downloadDocumentAdmin,
  getTuitionAdminDocuments,
  uploadDocumentTuition,
  patchTuitionAdminVerify,
  patchTuitionAdminEditDocument,
} from "../../../../../../store/slices/Tuition/tuitionAdminSlice";
import AdminVerifySection from "./AdminVerifySection";
import { buildVerifyPayload } from "./verifyPayload";
import { Form } from "../../../../../../components/common/Forms";
import {
  adminDocumentTypes,
  editFormFields,
} from "../../../../../../config/adminConfig";
import { trackButtonClick, trackDocumentUpload, trackFormError } from '../../../../../../services/analytics';
import { safeGet } from "../../../../../../utils/safeGet";

type TableColumn = {
  key: string;
  label: string;
};

interface DocumentTabProps {
  applicationData?: {
    empFirstName: string;
    empLastName: string;
    applicationId: string;
    employeeNuId: string;
  };
  documentsRefreshTrigger?: number;
}
const columns: TableColumn[] = [
  { key: "documentName", label: "Document name" },
  { key: "documentContentDesc", label: "Document content description" },
  { key: "createdTime", label: "Date added" },
  { key: "createdBy", label: "Uploaded by" },
  { key: "empUploadFlag", label: "Employee upload Flag" },
  { key: "editAction", label: "Edit" },
  { key: "downloadAction", label: "Download" },
];

type TableRow = {
  documentName: string;
  documentContentDesc: string;
  createdTime: string;
  createdBy: string;
  empUploadFlag: string;
  documentId: string;
};

const DownloadButton = ({ onClick }: { onClick?: () => void }) => (
  <button
    className="button -primary -small whitespace-nowrap flex items-center min-w-max"
    onClick={onClick}
  >
    <img
      src={downloadOutline}
      alt="download icon"
      className="w-6! h-6! flex-shrink-0"
      aria-hidden="true"
    />
    <span className="whitespace-nowrap">Download</span>
  </button>
);

const EditButton = ({ onClick, isActive }: { onClick?: () => void; isActive?: boolean }) => (
  <button
    className={`button ${isActive ? "bg-[#003B71]! text-white!" : "-secondary"} -small inline-flex items-center whitespace-nowrap min-w-max!`}
    onClick={onClick}
  >
    <span className="whitespace-nowrap">Edit file</span>
  </button>
);

const Documents: FC<DocumentTabProps> = ({
  applicationData,
  documentsRefreshTrigger,
}) => {
  const { applicationId = "", employeeNuId = "" } = applicationData ?? {};
  const [showSuccessToast, setShowSuccessToast] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [contentDescription, setContentDescription] = useState<string>("");
  const [validationError, setValidationError] = useState<string>(""); // for upload
  const [fileFormatError, setFileFormatError] = useState<string>("");
  const [fileSizeError, setFileSizeError] = useState<string>("");
  const [uploadError, setUploadError] = useState<string>("");
  const [documents, setDocuments] = useState<TableRow[]>([]);
   const [downloadError, setDownloadError] = useState<string>("");
  const { loading, applicationDetailsError } = useAppSelector(
    (state: any) => state.viewDocument
  );
  // Also track admin slice loading for status-related admin actions
  const { loading: adminLoading } = useAppSelector(
    (state: any) => safeGet(state.tuitionAdmin, { loading: false })
  );
  const dispatch = useDispatch();
  const location = useLocation();
  const createdDate = location.state?.createdDate;

  // Admin Document Types state
  const [selectedDocTypes, setSelectedDocTypes] = useState<string[]>([]);
  const [showEditSection, setShowEditSection] = useState(false);
  const [editingDocument, setEditingDocument] = useState<TableRow | null>(null);
  const [editDocumentName, setEditDocumentName] = useState<string>("");
  const [editDescription, setEditDescription] = useState<string>("");
  const [editCheckbox, setEditCheckbox] = useState<boolean>(false);
  const editFileInputRef = useRef<HTMLInputElement>(null);
  const [editSelectedFile, setEditSelectedFile] = useState<File | null>(null);
  const [editFileFormatError, setEditFileFormatError] = useState<string>("");
  const [editFileSizeError, setEditFileSizeError] = useState<string>("");
  const [showAdminSuccessToast, setShowAdminSuccessToast] = useState(false);
  const [editValidationError, setEditValidationError] = useState<string>("");
  const [showEditSuccessToast, setShowEditSuccessToast] = useState(false);
  const successToastRef = useRef<HTMLDivElement>(null);
  const editScrollTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    // Scroll to success toast when it appears
  useEffect(() => {
    if ((showSuccessToast && successToastRef.current) || (showEditSuccessToast && successToastRef.current)) {
      successToastRef.current.scrollIntoView({ behavior: "smooth", block: "center" });
    }
  }, [showSuccessToast, showEditSuccessToast]);

  // Clear the edit-section scroll timer on unmount so it can't fire after teardown
  useEffect(() => {
    return () => {
      if (editScrollTimeoutRef.current) {
        clearTimeout(editScrollTimeoutRef.current);
      }
    };
  }, []);
  const formatDate = (date: string) => {
    if (!date) return "";
    return date.replaceAll('-', "/");
  };
  // Hide success toast and all error banners when applicationId changes (for new application)
    useEffect(() => {
      setShowSuccessToast(false);
      setFileFormatError("");
      setFileSizeError("");
      setUploadError("");
      setValidationError("");
      setEditFileFormatError("");
      setEditFileSizeError("");
      setEditValidationError("");
      setSelectedFile(null);
        setContentDescription("");
        // for reseting edit section on application change
        setShowEditSection(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    }, [applicationId]);
 
  // Helper to compute selected doc types from API payload flags
  const computeSelectedDocTypes = (payload: any): string[] => {
    const mappings: Array<[keyof typeof payload | string, string]> = [
      ["gradesVerified", "gradesVerified"],
      ["paystubVerified", "paystubVerified"],
      ["receiptVerified", "receiptVerified"],
      ["scheduleVerified", "scheduleVerified"],
      ["verified5", "travelReceipts"],
      ["verified6", "other"],
    ];
    return mappings.reduce((acc: string[], [flagKey, label]) => {
      if (payload?.[flagKey] === "Y") acc.push(label);
      return acc;
    }, []);
  };

  const fetchDocuments = async () => {
    try {
      const action = await dispatch<any>(
        getTuitionAdminDocuments({ applicationId, nuid: employeeNuId })
      );

      if (!getTuitionAdminDocuments.fulfilled.match(action)) {
        setDocuments([]);
        setSelectedDocTypes([]);
        return;
      }

      const payload = action.payload;
      if (!payload?.documents) {
        setDocuments([]);
        setSelectedDocTypes([]);
        return;
      }

      setDocuments(payload.documents);
      setSelectedDocTypes(computeSelectedDocTypes(payload));
    } catch (error) {
      console.error("Failed to fetch documents:", error);
      setDocuments([]);
      setSelectedDocTypes([]);
    }
  };

  useEffect(() => {
    fetchDocuments();
  }, [applicationId, employeeNuId, documentsRefreshTrigger]);

  // Auto-dismiss download error after 10 seconds
  useEffect(() => {
    if (!downloadError) return;
    
    const timer = setTimeout(() => {
      setDownloadError("");
    }, 10000);

    return () => clearTimeout(timer);
  }, [downloadError]);

  useEffect(() => {
    const timer = setTimeout(() => {
      try {
        characterCountInit();
        console.log("Character count initialized");
      } catch (error) {
        console.error("Character count init error:", error);
      }
    }, 1000);

    return () => clearTimeout(timer);
  }, []);

  const handleChooseFile = () => fileInputRef.current?.click();

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setUploadError("");
    // File checks run on selection, independent of the description, and the file stays selected.
    const formatOk = validateFileFormatLocal(file);
    if (formatOk) validateFileSizeLocal(file);
    setSelectedFile(file);
    console.log("Selected file:", file.name);
  };

  const handleCancel = () => {
    
    setSelectedFile(null);
    setContentDescription("");
    setValidationError("");
    setFileFormatError("");
    setFileSizeError("");
    setUploadError("");
    const textarea = document.getElementById(
      "text-area-6"
    ) as HTMLTextAreaElement;
    if (textarea) {
      textarea.value = "";
    }

    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const handleContentChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setContentDescription(e.target.value);
    if (validationError && e.target.value.trim()) {
      setValidationError("");
    }
  };

  // Helpers to reduce cognitive complexity in handleUpload

  const getTextarea = () =>
    document.getElementById("text-area-6") as HTMLTextAreaElement | null;

  const clearTextarea = () => {
    const textarea = getTextarea();
    if (textarea) textarea.value = "";
  };

  const scrollToTextarea = () => {
    const textarea = getTextarea();
    if (textarea) {
      textarea.scrollIntoView({ behavior: "smooth", block: "center" });
      textarea.focus();
    }
  };

  const validateFileFormatLocal = (file: File): boolean => {
    const fileFormatData = {
      documentFile: file.name,
      documentFileSize: 0,
      contentDescription: "",
    };

    const fileFormatValidation = validateForm(
      uploadDocumentFields.filter(
        (field: { name: string }) => field.name === "documentFile"
      ),
      fileFormatData
    );

    if (!fileFormatValidation.isValid && fileFormatValidation.errors["documentFile"]) {
      setFileFormatError(fileFormatValidation.errors["documentFile"]);
      setFileSizeError("");
      return false;
    }
    setFileFormatError("");
    return true;
  };

  const validateFileSizeLocal = (file: File): boolean => {
    const fileSizeData = {
      documentFile: file.name,
      documentFileSize: file.size,
      contentDescription: "",
    };

    const fileSizeValidation = validateForm(
      uploadDocumentFields.filter(
        (field: { name: string }) => field.name === "documentFileSize"
      ),
      fileSizeData
    );

    if (!fileSizeValidation.isValid && fileSizeValidation.errors["documentFileSize"]) {
      setFileSizeError(fileSizeValidation.errors["documentFileSize"]);
      setFileFormatError("");
      return false;
    }
    setFileSizeError("");
    return true;
  };

  const validateContentLocal = (file: File, descriptionText: string): boolean => {
    const fullFormData = {
      documentFile: file.name,
      documentFileSize: file.size,
      contentDescription: descriptionText,
    };

    const contentValidation = validateForm(uploadDocumentFields, fullFormData);

    if (!contentValidation.isValid) {
      const msg =
        contentValidation.requiredIfErrors?.["contentDescription"] ??
        contentValidation.errors["contentDescription"];

      if (msg) {
        setValidationError(msg);
        scrollToTextarea();
        return false;
      }
    }
    setValidationError("");
    return true;
  };

  const handleUpload = async () => {
    console.log('📊 Analytics: Upload document clicked')
    trackButtonClick('Upload', 'tuition-admin-documents')
    if (!selectedFile) {
      setUploadError("Please choose a document to upload.");
      trackFormError('tuition', ['documentFile'])
      return;
    }

    // Validate file format, size, and content description
    if (!validateFileFormatLocal(selectedFile)) {
      trackFormError('tuition', ['documentFileFormat'])
      return;
    }
    if (!validateFileSizeLocal(selectedFile)) {
      trackFormError('tuition', ['documentFileSize'])
      return;
    }
    if (!validateContentLocal(selectedFile, contentDescription)) {
      trackFormError('tuition', ['contentDescription'])
      return;
    }

    console.log("Uploading file:", selectedFile.name);
    console.log("Content description:", contentDescription);

    // Reset form after successful upload
    if (selectedFile && applicationId) {
      const description = safeGet(getTextarea()?.value, "");
      trackDocumentUpload('tuition', selectedFile.name, true)

      try {
        const action = await dispatch<any>(
          uploadDocumentTuition({
            applicationId: applicationId,
            file: selectedFile,
            description,
            nuid: employeeNuId,
          })
        );

        if (uploadDocumentTuition.fulfilled.match(action)) {
          setShowSuccessToast(true);
          setTimeout(() => setShowSuccessToast(false), 25000);

          setSelectedFile(null);
          setContentDescription("");
          setUploadError(""); // Clear any previous errors
          fetchDocuments();
          clearTextarea();
        } else {
          console.error("Failed to upload document:", action.payload);
        }
      } catch (error) {
        console.error("Error during document upload:", error);
      }
    } else {
      setUploadError("Please select a file and ensure application ID is available.");
    }
  };

  const handleDownload = async (documentId: string, fileName?: string) => {
    console.log(`📊 Analytics: Download clicked - Document ${documentId}`)
    trackButtonClick('Download', 'tuition-admin-documents', { documentId })
    if (!documentId) {
      console.error("No application id provided.");
      return;
    }
    // Clear any previous download error
    setDownloadError("");
    const action = await dispatch<any>(
      downloadDocumentAdmin({
        applicationId,
        documentId,
        fileName,
        nuid: employeeNuId,
      })
    );

    if (downloadDocumentAdmin.fulfilled.match(action)) {
      console.log("Document downloaded successfully.");
      trackDocumentUpload('tuition', fileName || documentId, true)
    } else {
      console.error("Failed to download document:", action.payload);
      setDownloadError(action.payload as string || "Failed to download document");
    }
  };

  // Admin Document Types handlers
  const handleDocTypeToggle = (key: string) => {
    setSelectedDocTypes((prev) =>
      prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]
    );
  };

  const handleDocTypesClear = () => {
    setSelectedDocTypes([]);
  };

  const handleDocTypesSave = async () => {
    if (!applicationId) {
      console.error("Cannot save verification: missing applicationId");
      return;
    }

    const data = buildVerifyPayload(selectedDocTypes);
    try {
      const action = await dispatch<any>(
        patchTuitionAdminVerify({ applicationId, data, nuid: employeeNuId })
      );
      if (patchTuitionAdminVerify.fulfilled.match(action)) {
        // Show success notification
        setShowAdminSuccessToast(true);
        setTimeout(() => setShowAdminSuccessToast(false), 5000);
        console.log("Verification saved successfully");
      } else {
        console.error("Failed to save verification:", action.payload);
      }
    } catch (error) {
      console.error("Error during verification save:", error);
    }
  };

  // Edit section handlers
  const handleEditClick = (doc: TableRow) => {
    setEditingDocument(doc);
    setEditDocumentName(doc.documentName);
    setEditDescription(doc.documentContentDesc);
    setEditCheckbox(doc.empUploadFlag === "Y");
    setEditFileFormatError("");
    setEditFileSizeError("");
    setEditSelectedFile(null);
    setEditValidationError("");
    setShowEditSection(true);
    // Scroll to edit section
    editScrollTimeoutRef.current = setTimeout(() => {
      const editSection = document.getElementById("edit-section");
      if (editSection) {
        editSection.scrollIntoView({ behavior: "smooth", block: "start" });
      }
    }, 100);
  };
  const handleEditCheckboxChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setEditCheckbox(e.target.checked);
  };
  const handleEditChooseFile = () => editFileInputRef.current?.click();
  const handleEditFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setEditFileFormatError("");
    setEditFileSizeError("");
    setEditSelectedFile(file);
    // Validate on selection; the file stays selected and handleEditSave re-validates.
    if (!validateEditFileFormat(file)) return;
    validateEditFileSize(file);
  };

  // Helper function to scroll to edit section
  const scrollToEditSection = () => {
    setTimeout(() => {
      const editSection = document.getElementById("edit-section");
      if (editSection) {
        editSection.scrollIntoView({ behavior: "smooth", block: "start" });
      }
    }, 100);
  };

  // Helper function to validate edit file format
  const validateEditFileFormat = (file: File): boolean => {
    const allowedExtensions = /\.(pdf|jpg|jpeg|png|doc|docx)$/i;
    if (!allowedExtensions.test(file.name)) {
      setEditFileFormatError("Only PDF, JPG, PNG, DOC, and DOCX files are allowed.");
      setEditFileSizeError("");
      scrollToEditSection();
      return false;
    }
    return true;
  };

  // Helper function to validate edit file size
  const validateEditFileSize = (file: File): boolean => {
    const maxSizeInBytes = 10 * 1024 * 1024; // 10MB
    if (file.size > maxSizeInBytes) {
      setEditFileSizeError("File size must not exceed 10 MB.");
      setEditFileFormatError("");
      scrollToEditSection();
      return false;
    }
    return true;
  };

  // Helper function to build edit payload
  const buildEditPayload = (): any => {
    if (editSelectedFile) {
      const fd = new FormData();
      fd.append("file", editSelectedFile);
      fd.append("description", safeGet(editDescription, ""));
      fd.append("eeFlag", String(editCheckbox));
      return fd;
    }
    return {
      eeFlag: editCheckbox,
      description: safeGet(editDescription, safeGet(editDocumentName, "")),
    };
  };

  const handleEditSave = async () => {
    if (!editingDocument) return;
        // Validate description is not empty
    if (!editDescription || editDescription.trim() === "") {
      setEditValidationError("Document content description is required.");
      // Scroll to edit section to show error
      scrollToEditSection();
      return;
    }
 
    // Clear validation error if description is valid
    setEditValidationError("");

    // If a new file is being uploaded, validate it before proceeding
    if (editSelectedFile) {
      if (!validateEditFileFormat(editSelectedFile)) return;
      if (!validateEditFileSize(editSelectedFile)) return;

      // Clear errors if validation passed
      setEditFileFormatError("");
      setEditFileSizeError("");
    }

    // Persist edit via API (admin patch)
    try {
      const dataPayload = buildEditPayload();
      const action = await dispatch<any>(
        patchTuitionAdminEditDocument({
          applicationId,
          documentId: editingDocument.documentId,
          data: dataPayload,
          nuid: employeeNuId,
        })
      );
      if (patchTuitionAdminEditDocument.fulfilled.match(action)) {
        // Update local documents array (optimistic update)
        fetchDocuments();
        
        // Hide edit section
        setShowEditSection(false);
        setEditingDocument(null);

        // Show success notification for 5 seconds
        setShowEditSuccessToast(true);
        setTimeout(() => {
          setShowEditSuccessToast(false);
        }, 5000);
      } else {
        console.error(
          "Failed to edit document:",
          safeGet(action.payload, action.error)
        );
      }
    } catch (e) {
      console.error("Edit document patch error:", e);
    }
  };

  const handleEditCancel = () => {
    // Close the edit section and clear local edit state
    setShowEditSection(false);
    setEditingDocument(null);
    setEditDescription("");
    setEditCheckbox(false);
    setEditSelectedFile(null);
    setEditFileFormatError("");
    setEditFileSizeError("");
    setEditValidationError("");
    if (editFileInputRef.current) editFileInputRef.current.value = "";
    
  };

  // Show error if application details fetch failed
  if (applicationDetailsError) {
    return (
      <div className="margin-top-4u flex items-center justify-center min-h-[400px]">
        <div className="w-full max-w-md">
          <div className="flex flex-col items-center text-center">
            <div className="mb-4">
              <img
                src="/assets/images/errorCircleSmall.svg"
                alt="error icon"
                className="w-12 h-12 mx-auto"
              />
            </div>
            <h3 className="h3 text-red-600 mb-2">Error</h3>
            <p className="text-gray-700 text-sm">Document details not found</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="margin-top-4u">
      {(loading || adminLoading) && <Loader />}
           {downloadError && (
        <div className="margin-top-4u lg:mx-[40px]!">
          <UploadErrorBanner
            header="Download failed"
            message={downloadError}
            className="w-[70%]"
          />
        </div>
      )}
      <div>
        <p className="p3 -book  ml-5!">
          On this page, you can manage documents associated with your
          application. You have two main actions:
        </p>
        <ul className="list-disc ml-5!">
          <li className="ml-5!">
            <b>Edit Documents:</b> Review the list of uploaded documents and
            make edits as needed. You can click <b>Edit files</b> to update
            details or replace a file.
          </li>
          <li className="ml-5!">
            <b>Download Documents:</b> If you need to save a copy of any
            uploaded document, click <b>Download</b> next to the file name.
          </li>
          <li className="ml-5!">
            <b>Upload Documents:</b> Use the section below to add new documents
            to your application. Follow the upload guidelines provided.
          </li>
          <li className="ml-5!">
            <b>Admin Section:</b> If you are an administrator, scroll to the{" "}
            <b>For Admin use only</b> section at the bottom of the page to
            verify document types and complete the application review.
          </li>
        </ul>
      </div>
      <div className="margin-top-4u lg:mx-[40px]!">
        <CardTable
          className="!mb-[61px] px-4 mx-5 sm:mx-0 sm:px-0"
          aria-label="My documents table"
        >
          <h3 className="h3">My documents</h3>
          <div className="lg:mt-[20px]!">
            {documents.length > 0 ? (
              <>
                <p className="p3 -book mb-4!">
                  Documents associated with this application
                </p>
                <Table
                  columns={columns}
                  rows={documents.map((row) => ({
                    ...row,
                    // Show only date (MM/DD/YYYY), omit time
                    createdTime: formatTimestampMDYHMS(row.createdTime).split(" ")[0],
                    editAction: (
                      <EditButton 
                        onClick={() => handleEditClick(row)} 
                        isActive={showEditSection && editingDocument?.documentId === row.documentId}
                      />
                    ),
                    downloadAction: (
                      <DownloadButton
                        onClick={() =>
                          handleDownload(row.documentId, row.documentName)
                        }
                      />
                    ),
                  }))}
                />
              </>
            ) : (
              !loading && (
                <p className="p3 -book text-center mt-[10px]!">
                  No documents found for this application.
                </p>
              )
            )}
          </div>
        </CardTable>
      </div>

      {/* Edit Section */}
      {showEditSection && editingDocument && (
        <div id="edit-section" className="margin-top-4u lg:mx-[40px]!">
          <h3 className="h3">Editing document - {editDocumentName} </h3>

          {/* Error Banners at top of edit section */}
          {editFileFormatError && (
            <UploadErrorBanner
              header="Incorrect file type"
              message="The selected file type is not supported. Please upload a PDF, JPG, PNG, DOC, or DOCX file."
              className="w-[70%] mt-4"
            />
          )}

          {editFileSizeError && (
            <UploadErrorBanner
              header="File size exceeds limit"
              message="The selected file is too large. Please upload a file that is 10 MB or smaller."
              className="w-[70%] mt-4"
            />
          )}
        {/* Validation Error Banner */}
          {editValidationError && (
            <UploadErrorBanner
              header="Document content description is missing"
              message="Please add a description for the document before saving."
              className="w-[70%] mt-4"
            />
          )}
          <Form
            fields={editFormFields}
            initialValues={{
              fileName: editDocumentName,
              description: editDescription,
            }}
            externalValidationErrors={
              editValidationError ? { description: editValidationError } : {}
            }
            onValueChange={(name, value) => {
              if (name === "description") {
                setEditDescription(value);
                // Clear validation error when user starts typing
                if (editValidationError && value.trim()) {
                  setEditValidationError("");
                }
              }
            }}
            buttons={[]}
            className="!mt-4"
          />

        

          {/* Edit: Replace file (optional) */}
          <div className="inline-flex items-center gap-2 border border-gray-300 bg-[#F7F7F7] text-[#878F9B] !p-4 !mt-[20px]">
            <input
              type="file"
              ref={editFileInputRef}
              style={{ display: "none" }}
              onChange={handleEditFileChange}
              accept=".pdf,.jpg,.jpeg,.png,.doc,.docx"
              data-testid="edit-file-input"
            />

            <button
              className="button whitespace-nowrap flex items-center gap-2 flex-shrink-0"
              onClick={handleEditChooseFile}
            >
              <img
                src={uploadOutline}
                alt="upload icon"
                className="!w-[1.6rem] !h-[1.25rem] flex-shrink-0"
                aria-hidden="true"
              />
              <span>Choose File</span>
            </button>

              {(() => {
            if (editFileFormatError) {
              return (
                <p className="p3 !mt-0 !text-[12px] text-[#D1480C] flex items-center gap-2">
                  <img
                    src="/assets/images/errorCircleSmall.svg"
                    alt="Error icon"
                    className="!w-[16px] !h-[16px]"
                  />
                  {editFileFormatError}
                </p>
              );
            }

            if (editFileSizeError) {
              return (
                <p className="p3 !mt-0 !text-[12px] text-[#D1480C] flex items-center gap-2">
                  <img
                    src="/assets/images/errorCircleSmall.svg"
                    alt="Error icon"
                    className="!w-[16px] !h-[16px]"
                  />
                  {editFileSizeError}
                </p>
              );
            }

            return <p className="p3 -dolphin !mt-0">Max file size 10 MB</p>;
          })()}
          
          </div>
          {editSelectedFile && (
          <div className="mt-2">
            <span>Selected file: {editSelectedFile.name}</span>
          </div>
        )}
        {/* Checkbox below */}
          <div className="margin-top-2u">
            <label className="flex items-center cursor-pointer">
              <input
                type="checkbox"
                className="cursor-pointer scale-75"
                checked={editCheckbox}
                onChange={handleEditCheckboxChange}
              />
              <span className="p4 -book !ml-[30px] mt-[10px]!">
                Toggle EE load flag
              </span>
            </label>
          </div>
          <div className="margin-top-2u flex gap-3">
            <button className="button -primary" onClick={handleEditSave}>
              Save
            </button>
            <button className="button -secondary" onClick={handleEditCancel}>
              Cancel
            </button>
          </div>
        </div>
      )}
      <hr className="-variation-1 !py-[30px]" />
      
      {/* Edit Success Notification */}
      {showEditSuccessToast && (
            <div ref={successToastRef}>
        <Notifications
          messageInfo="Document has been updated successfully."
          status={true}
        />
      </div>
      )}
      
      {showSuccessToast && (
        <div ref={successToastRef}>
        <Notifications
          messageInfo="Document has been uploaded successfully."
          status={true}
        />
      </div>
      )}
      <h2 className="h2">Upload documents</h2>

      {/* Upload Section */}
      <div>
        <p className="p3 -book">
          upload documents for application submitted on{" "}
          {formatDate(createdDate)}
        </p>

        <h3 className="h3 margin-top-2u">How to upload:</h3>
        <p className="p3 -book">
          To upload a document, please follow the steps below.
        </p>

        {/* Instructions */}
        <ol className="mb-4">
          <li className="p3 -boook mt-6!"> Select the application.</li>
          <li className="p3">
            1. Click <b>Choose File</b> to select the document.
          </li>
          <li className="p3">2. Enter a content description.</li>
          <li className="p3">
            3. Click <b>Upload</b> to attach the document.
          </li>
        </ol>

        <ul className="list-disc pl-6 !mt-0 marker:text-[#D1480C] !ml-[14px]">
          <li className="!mt-6 text-[#D1480C] p3 -bbok !mb p3 -book">
            Required fields
          </li>
          <li className="p3 -bbok">
            Upload New Document (Upload only PDF, JPG, PNG, DOC, and DOCX
            files).
          </li>
          <li className="p3 -book">
            You must provide a description of the contents when uploading a
            document.
          </li>
          <li className="p3 -book">
            Clear button to clear the input fields before uploading.
          </li>
        </ul>

        {fileFormatError && (
          <UploadErrorBanner
            header="Incorrect file type"
            message="The selected file type is not supported. Please upload a PDF, JPG, PNG, DOC, or DOCX file."
            className="w-[70%]"
          />
        )}

        {fileSizeError && (
          <UploadErrorBanner
            header="File size exceeds limit"
            message="The selected file is too large. Please upload a file that is 10 MB or smaller."
            className="w-[70%]"
          />
        )}

        {uploadError && (
          <UploadErrorBanner
            header="Document required"
            message={uploadError}
            className="w-[70%]"
          />
        )}

        <div className="inline-flex items-center gap-2 border border-gray-300 bg-[#F7F7F7] text-[#878F9B] !p-4 !mt-[20px]">
          <input
            type="file"
            ref={fileInputRef}
            style={{ display: "none" }}
            onChange={handleFileChange}
            accept=".pdf,.jpg,.jpeg,.png,.doc,.docx"
            data-testid="file-input"
          />

          <button
            className="button whitespace-nowrap flex items-center gap-2 flex-shrink-0"
            onClick={handleChooseFile}
          >
            <img
              src={uploadOutline}
              alt="upload icon"
              className="!w-[1.6rem] !h-[1.25rem] flex-shrink-0"
              aria-hidden="true"
            />
            <span>Choose File</span>
          </button>

          {(() => {
            if (fileFormatError) {
              return (
                <p className="p3 !mt-0 !text-[12px] text-[#D1480C] flex items-center gap-2">
                  <img
                    src="/assets/images/errorCircleSmall.svg"
                    alt="Error icon"
                    className="!w-[16px] !h-[16px]"
                  />
                  {fileFormatError}
                </p>
              );
            }

            if (fileSizeError) {
              return (
                <p className="p3 !mt-0 !text-[12px] text-[#D1480C] flex items-center gap-2">
                  <img
                    src="/assets/images/errorCircleSmall.svg"
                    alt="Error icon"
                    className="!w-[16px] !h-[16px]"
                  />
                  {fileSizeError}
                </p>
              );
            }

            return <p className="p3 -dolphin !mt-0">Max file size 10 MB</p>;
          })()}
        </div>

        {selectedFile && (
          <div className="mt-2">
            <span>Selected file: {selectedFile.name}</span>
          </div>
        )}
      </div>

      {validationError && (
        <UploadErrorBanner
          header="Upload document comment is missing"
          message="Please add a comment for the uploaded document before continuing."
          className="w-[70%]"
        />
      )}

      <div className="form-group margin-top-2u">
        <label
          id="textarea-label2"
          className="p4 -medium after:content-['*'] after:text-[#D1480C]"
          htmlFor="text-area-6"
          aria-required={true}
        >
          Describe the contents
        </label>
        <div className="textarea-wrapper">
          <textarea
            autoComplete="on"
            id="text-area-6"
            aria-labelledby="textarea-label2 textarea_count2"
            aria-describedby="textarea-error-description-list-2"
            className={`text-area_word-count !text-[#0D1C3D] text-area_global -book ${
              validationError && !contentDescription.trim() ? "field-error" : ""
            }`}
            data-show-lines="5"
            maxLength={1000}
            onChange={handleContentChange}
            value={contentDescription}
          ></textarea>
          {!(validationError && !contentDescription.trim()) && (
            <div className="character-count">
              <span
                id="textarea_count2"
                aria-live="polite"
                aria-atomic="true"
                className="count-area text-input-helper"
                data-description=" of 1000 characters left"
                data-full-text="No more characters available, 1000 of 1000"
              >
                {contentDescription.length === 0 ? `1000 of 1000 characters left` : `${1000 - contentDescription.length} of 1000 characters left`}
              </span>
            </div>
          )}
        </div>
        {validationError && !contentDescription.trim() && (
          <div className="error-description mt-2">
            <ul id="textarea-error-description-list-2">
              <li>
                <img
                  src="/assets/images/errorCircleSmall.svg"
                  alt="Error icon"
                />
                <span className="screenreader-only">Error</span>
                {validationError}
              </li>
            </ul>
          </div>
        )}
      </div>

      {/* Buttons */}
      <div className="flex flex-col gap-4 margin-top-2u sm:flex-row">
        <button className="button -light" onClick={handleCancel}>
          Clear information
        </button>

        <button className="button -primary -light" onClick={handleUpload}>
          Upload document
        </button>
      </div>

      <div className="relative !mt-6 !mb-4 flex gap-2">
        <img src={InfoIcon} alt="info icon" />
        <p className="p3 -book">
          If you select clear button you may need to re-enter your description
          and re-upload documentation
        </p>
      </div>

      {/* Admin Success Notification */}
      {showAdminSuccessToast && (
        <Notifications
          messageInfo="Document verification has been saved successfully."
          status={true}
        />
      )}

      {/* Admin section extracted to dedicated component */}
      <AdminVerifySection
        adminDocumentTypes={adminDocumentTypes}
        selectedDocTypes={selectedDocTypes}
        onToggle={handleDocTypeToggle}
        onClear={handleDocTypesClear}
        onSave={handleDocTypesSave}
      />
    </div>
  );
};

export default Documents;